import { RadioGroup } from "@headlessui/react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Badge } from "@/components/elements/Badge/Badge";
import { RadioButtonCard } from "@/components/elements/ButtonCard/ButtonCard";
import { InputDescription } from "@/components/primitives/InputDescription";
import FieldLabel from "@/components/primitives/InputLabel";
import cn from "@/helpers/classNames";
import { FormType } from "@/integrations";
import { FieldErrorMessage } from "@/components/primitives/FieldErrorMessage";
import { useUnmountEffect } from "@/hooks/useUnmountEffect";

import FormFactory from "./FormFactory";

/** Unregisters its fields on unmount, so switching options submits only the selected option's values. */
function SelectedOptionFields({
  fields,
  editMode,
}: {
  fields: FormType[];
  editMode?: boolean;
}) {
  const { unregister } = useFormContext();

  useUnmountEffect(() => {
    fields.forEach((field) => {
      unregister(field.name);
    });
  });

  return (
    <div className="mt-4 space-y-4">
      {fields.map((field, i) => (
        <FormFactory key={i} form={field} editMode={editMode} />
      ))}
    </div>
  );
}

export default function ChoiceForm({
  field,
  editMode,
}: {
  field: Extract<FormType, { type: "choice" }>;
  editMode?: boolean;
}) {
  const { name, label, required, description, options, disabled } = field;

  const { control } = useFormContext();
  const selectedValue = useWatch({ control, name });
  const selectedOption = options.find(
    (option) => option.value === selectedValue,
  );

  return (
    <div>
      <FieldLabel id={name} required={required}>
        {label}
      </FieldLabel>
      {!!description && <InputDescription>{description}</InputDescription>}
      <Controller
        name={name}
        control={control}
        rules={{ required: required && "This field is required" }}
        render={({ field: { value, onChange } }) => (
          <RadioGroup
            value={value ?? null}
            onChange={onChange}
            className="mt-2 grid grid-cols-1 items-stretch gap-2 sm:grid-cols-2"
          >
            {options.map((option) => (
              <RadioGroup.Option
                key={option.value}
                value={option.value}
                disabled={disabled || option.disabled}
                className="flex"
              >
                {({ checked }) => (
                  <RadioButtonCard
                    isChecked={checked}
                    disabled={disabled || option.disabled}
                    className="w-full flex-1 items-start gap-3 p-4 text-left whitespace-normal dark:bg-gray-900/20"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
                        checked
                          ? "border-blue-600 dark:border-blue-400"
                          : "border-gray-300 dark:border-gray-600",
                      )}
                    >
                      {checked && (
                        <span className="size-2 rounded-full bg-blue-500 dark:bg-blue-400" />
                      )}
                    </span>
                    <span className="flex flex-col gap-1">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-semibold">
                          {option.label}
                        </span>
                        {!!option.recommended && (
                          <Badge variant="info">Recommended</Badge>
                        )}
                      </span>
                      {!!option.description && (
                        <span className="text-xs text-muted-foreground">
                          {option.description}
                        </span>
                      )}
                    </span>
                  </RadioButtonCard>
                )}
              </RadioGroup.Option>
            ))}
          </RadioGroup>
        )}
      />
      <FieldErrorMessage name={name} />
      {!!selectedOption?.fields?.length && (
        <SelectedOptionFields
          key={selectedOption.value}
          fields={selectedOption.fields}
          editMode={editMode}
        />
      )}
    </div>
  );
}
